import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const PersonalDetailsTab = ({ profile, employee, onUpdate, canEdit, onSave, onCancel, saving }) => {
  const [editedData, setEditedData] = useState({
    personalDetails: {
      dateOfBirth: '',
      gender: '',
      maritalStatus: '',
      nationality: '',
      religion: '',
      bloodGroup: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      },
      emergencyContact: {
        name: '',
        relationship: '',
        phone: '',
        email: ''
      },
      pfNumber: '',
      esiNumber: '',
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      educationQualification: '',
      professionalExperience: ''
    }
  });
  const [errors, setErrors] = useState({});
  // CRITICAL FIX: Add render key to force complete re-render
  const [renderKey, setRenderKey] = useState(0);

  // Define dropdown options
  const genderOptions = [
    { value: '', label: 'Select Gender' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  const maritalStatusOptions = [
    { value: '', label: 'Select Status' },
    { value: 'single', label: 'Single' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'widowed', label: 'Widowed' }
  ];

  const bloodGroupOptions = [
    { value: '', label: 'Select Blood Group' },
    { value: 'A+', label: 'A+' },
    { value: 'A-', label: 'A-' },
    { value: 'B+', label: 'B+' },
    { value: 'B-', label: 'B-' },
    { value: 'AB+', label: 'AB+' },
    { value: 'AB-', label: 'AB-' },
    { value: 'O+', label: 'O+' },
    { value: 'O-', label: 'O-' }
  ];

  const religionOptions = [
    { value: '', label: 'Select Religion' },
    { value: 'christianity', label: 'Christianity' },
    { value: 'islam', label: 'Islam' },
    { value: 'hinduism', label: 'Hinduism' },
    { value: 'buddhism', label: 'Buddhism' },
    { value: 'judaism', label: 'Judaism' },
    { value: 'sikhism', label: 'Sikhism' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  // CRITICAL FIX: Force complete component re-render when canEdit changes
  useEffect(() => {
    if (employee) {
      console.log('🔄 PersonalDetailsTab: FORCING COMPLETE RE-RENDER - canEdit:', canEdit);
      
      setEditedData({
        personalDetails: {
          dateOfBirth: employee?.personalDetails?.dateOfBirth || '',
          gender: employee?.personalDetails?.gender || '',
          maritalStatus: employee?.personalDetails?.maritalStatus || '',
          nationality: employee?.personalDetails?.nationality || '',
          religion: employee?.personalDetails?.religion || '',
          bloodGroup: employee?.personalDetails?.bloodGroup || '',
          address: {
            street: employee?.personalDetails?.address?.street || '',
            city: employee?.personalDetails?.address?.city || '',
            state: employee?.personalDetails?.address?.state || '',
            zipCode: employee?.personalDetails?.address?.zipCode || ''
          },
          emergencyContact: {
            name: employee?.personalDetails?.emergencyContact?.name || employee?.personalDetails?.emergencyContactName || '',
            relationship: employee?.personalDetails?.emergencyContact?.relationship || employee?.personalDetails?.emergencyContactRelationship || '',
            phone: employee?.personalDetails?.emergencyContact?.phone || employee?.personalDetails?.emergencyContactPhone || '',
            email: employee?.personalDetails?.emergencyContact?.email || ''
          },
          pfNumber: employee?.personalDetails?.pfNumber || '',
          esiNumber: employee?.personalDetails?.esiNumber || '',
          bankName: employee?.personalDetails?.bankName || '',
          accountNumber: employee?.personalDetails?.accountNumber || '',
          ifscCode: employee?.personalDetails?.ifscCode || '',
          educationQualification: employee?.personalDetails?.educationQualification || '',
          professionalExperience: employee?.personalDetails?.professionalExperience || ''
        }
      });
      
      // CRITICAL: Increment render key to force unmount/remount of all inputs
      setRenderKey(prev => prev + 1);
      
      console.log('✅ PersonalDetailsTab: Complete re-render triggered, new renderKey:', renderKey + 1);
    }
  }, [employee?.id, canEdit]);

  // CRITICAL FIX: Update parent immediately when field changes (for real-time updates)
  const handleFieldChange = (field, value) => {
    const fieldParts = field?.split('.');
    
    setEditedData(prev => {
      let updatedPersonalDetails;
      
      if (fieldParts?.length === 2) {
        // personalDetails.field
        updatedPersonalDetails = {
          ...prev?.personalDetails,
          [fieldParts?.[1]]: value
        };
      } else if (fieldParts?.length === 3) {
        // personalDetails.address.field or personalDetails.emergencyContact.field
        const nestedObject = fieldParts?.[1];
        const nestedField = fieldParts?.[2];
        
        updatedPersonalDetails = {
          ...prev?.personalDetails,
          [nestedObject]: {
            ...prev?.personalDetails?.[nestedObject],
            [nestedField]: value
          }
        };
      } else {
        return prev;
      }
      
      // FIXED: Update parent immediately with changes
      const updatedData = {
        personalDetails: updatedPersonalDetails
      };
      
      if (onUpdate) {
        onUpdate(updatedData);
      }
      
      return updatedData;
    });
  };

  // CRITICAL: Add console log to verify disabled state
  console.log('🎯 PersonalDetailsTab render - canEdit:', canEdit, 'disabled will be:', !canEdit);

  return (
    <div className="space-y-6">
      {/* ENHANCED: More prominent edit mode indicator */}
      {canEdit && (
        <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 bg-green-500 rounded-full">
              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
            </div>
            <div>
              <p className="text-sm font-bold text-green-800">
                ✏️ EDIT MODE ACTIVE
              </p>
              <p className="text-xs text-green-700 mt-1">
                All personal detail fields are now editable. Click to modify any information.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Basic Personal Information */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Basic Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Input
            key={`dateOfBirth-${renderKey}`}
            label="Date of Birth"
            type="date"
            value={editedData?.personalDetails?.dateOfBirth || ''}
            onChange={(e) => handleFieldChange('personalDetails.dateOfBirth', e?.target?.value)}
            readOnly={!canEdit}
          />
          
          <Select
            key={`gender-${renderKey}`}
            label="Gender"
            options={genderOptions}
            value={editedData?.personalDetails?.gender || ''}
            onValueChange={(value) => handleFieldChange('personalDetails.gender', value)}
            disabled={!canEdit}
            placeholder="Select Gender"
            name="gender"
            error=""
            helperText=""
          />

          <Select
            key={`maritalStatus-${renderKey}`}
            label="Marital Status"
            options={maritalStatusOptions}
            value={editedData?.personalDetails?.maritalStatus || ''}
            onValueChange={(value) => handleFieldChange('personalDetails.maritalStatus', value)}
            disabled={!canEdit}
            placeholder="Select Status"
            name="maritalStatus"
            error=""
            helperText=""
          />

          <Select
            key={`bloodGroup-${renderKey}`}
            label="Blood Group"
            options={bloodGroupOptions}
            value={editedData?.personalDetails?.bloodGroup || ''}
            onValueChange={(value) => handleFieldChange('personalDetails.bloodGroup', value)}
            disabled={!canEdit}
            placeholder="Select Blood Group"
            name="bloodGroup"
            error=""
            helperText=""
          />

          <Select
            key={`religion-${renderKey}`}
            label="Religion"
            options={religionOptions}
            value={editedData?.personalDetails?.religion || ''}
            onValueChange={(value) => handleFieldChange('personalDetails.religion', value)}
            disabled={!canEdit}
            placeholder="Select Religion"
            name="religion"
            error=""
            helperText=""
          />

          <Input
            key={`nationality-${renderKey}`}
            label="Nationality"
            value={editedData?.personalDetails?.nationality || ''}
            onChange={(e) => handleFieldChange('personalDetails.nationality', e?.target?.value)}
            readOnly={!canEdit}
            placeholder="Enter nationality"
          />
        </div>
      </div>

      {/* Address Section */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Address</h3>
        <div className="grid grid-cols-1 gap-6">
          <Input
            key={`address-street-${renderKey}`}
            label="Street Address"
            value={editedData?.personalDetails?.address?.street || ''}
            onChange={(e) => handleFieldChange('personalDetails.address.street', e?.target?.value)}
            readOnly={!canEdit}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Input
              key={`address-city-${renderKey}`}
              label="City"
              value={editedData?.personalDetails?.address?.city || ''}
              onChange={(e) => handleFieldChange('personalDetails.address.city', e?.target?.value)}
              readOnly={!canEdit}
            />
            
            <Input
              key={`address-state-${renderKey}`}
              label="State"
              value={editedData?.personalDetails?.address?.state || ''}
              onChange={(e) => handleFieldChange('personalDetails.address.state', e?.target?.value)}
              readOnly={!canEdit}
            />
            
            <Input
              key={`address-zipCode-${renderKey}`}
              label="ZIP Code"
              value={editedData?.personalDetails?.address?.zipCode || ''}
              onChange={(e) => handleFieldChange('personalDetails.address.zipCode', e?.target?.value)}
              readOnly={!canEdit}
            />
          </div>
        </div>
      </div>

      {/* Emergency Contact Section */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Emergency Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            key={`emergencyContact-name-${renderKey}`}
            label="Contact Name"
            value={editedData?.personalDetails?.emergencyContact?.name || ''}
            onChange={(e) => handleFieldChange('personalDetails.emergencyContact.name', e?.target?.value)}
            readOnly={!canEdit}
          />
          
          <Input
            key={`emergencyContact-relationship-${renderKey}`}
            label="Relationship"
            value={editedData?.personalDetails?.emergencyContact?.relationship || ''}
            onChange={(e) => handleFieldChange('personalDetails.emergencyContact.relationship', e?.target?.value)}
            readOnly={!canEdit}
          />
          
          <Input
            key={`emergencyContact-phone-${renderKey}`}
            label="Phone Number"
            type="tel"
            value={editedData?.personalDetails?.emergencyContact?.phone || ''}
            onChange={(e) => handleFieldChange('personalDetails.emergencyContact.phone', e?.target?.value)}
            readOnly={!canEdit}
          />
          
          <Input
            key={`emergencyContact-email-${renderKey}`}
            label="Email Address"
            type="email"
            value={editedData?.personalDetails?.emergencyContact?.email || ''}
            onChange={(e) => handleFieldChange('personalDetails.emergencyContact.email', e?.target?.value)}
            readOnly={!canEdit}
          />
        </div>
      </div>

      {/* Banking & Government Details Section */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Banking & Government Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Input
            key={`bankName-${renderKey}`}
            label="Bank Name"
            value={editedData?.personalDetails?.bankName || ''}
            onChange={(e) => handleFieldChange('personalDetails.bankName', e?.target?.value)}
            readOnly={!canEdit}
          />
          
          <Input
            key={`accountNumber-${renderKey}`}
            label="Account Number"
            value={editedData?.personalDetails?.accountNumber || ''}
            onChange={(e) => handleFieldChange('personalDetails.accountNumber', e?.target?.value)}
            readOnly={!canEdit}
          />
          
          <Input
            key={`ifscCode-${renderKey}`}
            label="IFSC Code"
            value={editedData?.personalDetails?.ifscCode || ''}
            onChange={(e) => handleFieldChange('personalDetails.ifscCode', e?.target?.value)}
            readOnly={!canEdit}
          />
          
          <Input
            key={`pfNumber-${renderKey}`}
            label="PF Number"
            value={editedData?.personalDetails?.pfNumber || ''}
            onChange={(e) => handleFieldChange('personalDetails.pfNumber', e?.target?.value)}
            readOnly={!canEdit}
          />
          
          <Input
            key={`esiNumber-${renderKey}`}
            label="ESI Number"
            value={editedData?.personalDetails?.esiNumber || ''}
            onChange={(e) => handleFieldChange('personalDetails.esiNumber', e?.target?.value)}
            readOnly={!canEdit}
          />
        </div>
      </div>

      {/* Professional Details Section */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Professional Details</h3>
        <div className="space-y-4">
          <Input
            key={`educationQualification-${renderKey}`}
            label="Education Qualification"
            value={editedData?.personalDetails?.educationQualification || ''}
            onChange={(e) => handleFieldChange('personalDetails.educationQualification', e?.target?.value)}
            readOnly={!canEdit}
          />
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Professional Experience
            </label>
            <textarea
              key={`professionalExperience-${renderKey}`}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary read-only:cursor-not-allowed read-only:bg-muted read-only:opacity-60"
              rows={4}
              placeholder="Enter professional experience details"
              value={editedData?.personalDetails?.professionalExperience || ''}
              onChange={(e) => handleFieldChange('personalDetails.professionalExperience', e?.target?.value)}
              readOnly={!canEdit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalDetailsTab;